import type { NavigationGuardWithThis, RouteRecordRaw } from "vue-router"
import { useAuthStore } from "@core/stores/auth.store"
import { ensureAuthUserLoaded } from "@core/router/building.routes"
import { useInterrupteursStore } from "@core/stores/interrupteurs.store"
import { destinationOptimTurpeV2 } from "./utils/optimturpe-v2.acces"
import PageBuildingOptimisationContractuelle from "./pages/PageBuildingOptimisationContractuelle.vue"
import PageBuildingOptimTurpeV2 from "./pages/PageBuildingOptimTurpeV2.vue"
import PageBuildingOptimTurpeV2Aide from "./pages/PageBuildingOptimTurpeV2Aide.vue"
import PageComparateurOffres from "./pages/PageComparateurOffres.vue"

/**
 * La garde de l'Optim TURPE V2, partagée par l'étude et par sa page d'aide :
 * module OptimTurpe + rôle ≥ employé write, PLUS l'interrupteur de runtime
 * `OPTIMTURPE_V2` (DEV-1892). Baissé, ou route des interrupteurs en échec,
 * l'adresse renvoie vers la v1 (ou la page compteur si la v1 est fermée) avant
 * que le moindre composant v2 ne soit monté.
 */
const gardeOptimTurpeV2: NavigationGuardWithThis<undefined> = async (
    to,
    _from,
    next
) => {
    const authStore = useAuthStore()
    await ensureAuthUserLoaded(authStore)
    // Idempotent, ne rejette jamais : déjà lu par le garde global, ou lu ici si
    // l'on arrive par une autre porte.
    const interrupteurs = useInterrupteursStore()
    await interrupteurs.charger()
    const destination = destinationOptimTurpeV2({
        hasModuleOptimTurpe: authStore.hasModuleOptimTurpe,
        isEmployeWriteOrHigher: authStore.isEmployeWriteOrHigher,
        optimturpeV2Arme: interrupteurs.optimturpeV2Arme,
    })
    const params = { _id_building: String(to.params._id_building) }
    if (destination === "v2") {
        next()
    } else if (destination === "v1") {
        next({ name: "PageBuildingOptimisationContractuelle", params })
    } else {
        next({ name: "PageBuildingMeter", params })
    }
}

export const buildingShellChildren = [
    {
        path: "courtage/comparateur-offres",
        name: "PageComparateurOffres",
        component: PageComparateurOffres,
        beforeEnter: async (to, _from, next) => {
            const authStore = useAuthStore()
            await ensureAuthUserLoaded(authStore)
            if (authStore.hasOfferCourtage) {
                next()
            } else {
                next({
                    name: "PageBuildingMeter",
                    params: {
                        _id_building: String(to.params._id_building),
                    },
                })
            }
        },
    },
    {
        path: "optimisation-contractuelle",
        name: "PageBuildingOptimisationContractuelle",
        component: PageBuildingOptimisationContractuelle,
        beforeEnter: async (to, _from, next) => {
            const authStore = useAuthStore()
            await ensureAuthUserLoaded(authStore)
            // Optimisation TURPE = analyse métier : ❌ pour le rôle Utilisateur
            // (table des droits DEV-16282). Même garde que le dashboard parc
            // (beforeEnterDashboardOptimTurpe).
            if (
                authStore.hasModuleOptimTurpe &&
                authStore.isEmployeWriteOrHigher
            ) {
                next()
            } else {
                next({
                    name: "PageBuildingMeter",
                    params: {
                        _id_building: String(to.params._id_building),
                    },
                })
            }
        },
    },
    {
        // Optim TURPE v2 (DEV-715/783 + série 789 + Lot D 788/787). Même garde que
        // la v1 (module OptimTurpe + rôle ≥ employé write), PLUS l'interrupteur de
        // runtime `OPTIMTURPE_V2` (DEV-1892) : baissé, ou route des interrupteurs
        // en échec, l'adresse directe renvoie vers la v1 (ou la page compteur si
        // la v1 est fermée) avant que le moindre composant v2 ne soit monté.
        path: "optimisation-contractuelle-v2",
        name: "PageBuildingOptimTurpeV2",
        component: PageBuildingOptimTurpeV2,
        beforeEnter: gardeOptimTurpeV2,
    },
    {
        // La page d'aide de la V2, « Comprendre l'étude » (DEV-2311, maquette
        // v0.3.2 du 06/10/2026). EXACTEMENT la garde de la V2 : personne
        // n'atteint l'aide sans voir l'étude qu'elle explique.
        path: "optimisation-contractuelle-v2/aide",
        name: "PageBuildingOptimTurpeV2Aide",
        component: PageBuildingOptimTurpeV2Aide,
        beforeEnter: gardeOptimTurpeV2,
    },
] as RouteRecordRaw[]
